import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, Info, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { anlopCode, hq } from "@/components/anlop/embedCodes";
import { PortTabs } from "@/components/anlop/PortTabs";
import { RegionCard } from "@/components/anlop/RegionCard";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CopyBox } from "@/components/pax/PaxPanel";
import { NoteEditor } from "@/components/admin/NoteEditor";
import { SourceCard } from "@/components/anlop/SourceCard";
import { AnlopPreview } from "@/components/anlop/AnlopPreview";
import { TeaserSettings } from "@/components/anlop/TeaserSettings";
import { AnlopDocs } from "@/components/anlop/AnlopDocs";

const H = ({ n, title, hint }) => (
  <div className="mb-4 flex items-start gap-3">
    {n && <span className="mt-0.5 w-7 h-7 shrink-0 rounded-full bg-[#0A192F] text-white text-sm font-semibold flex items-center justify-center font-num">{n}</span>}
    <div>
      <h2 className="font-display text-xl font-semibold text-slate-900">{title}</h2>
      {hint && <p className="text-sm text-slate-500 mt-0.5">{hint}</p>}
    </div>
  </div>
);

const useAnlop = (cid, pid, onSaved) => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rev, setRev] = useState(0);
  const timer = useRef(null);
  useEffect(() => {
    api.get(`/customers/${cid}/anlop${hq(pid)}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [cid, pid]);
  const persist = (s) => {
    setSaving(true);
    api.put(`/customers/${cid}/anlop${hq(pid)}`, { port_name: s.port_name || "", title: s.title.trim() || "Anløpsliste", show_title: s.show_title, intro: s.intro, note_html: s.note_html, ics_enabled: !!s.ics_enabled, teaser: s.teaser })
      .then(() => { setRev((r) => r + 1); onSaved(s); })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setSaving(false));
  };
  const update = (patch, delay = 700) => {
    setSaving(true);
    setData((d) => {
      const s = { ...d.settings, ...patch };
      clearTimeout(timer.current);
      timer.current = setTimeout(() => persist(s), delay);
      return { ...d, settings: s };
    });
  };
  const setAll = (d) => { setData(d); setRev((r) => r + 1); };
  return { data, saving, rev, update, setAll };
};

const Loading = () => <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-sky-700" /></div>;

const DeletePort = ({ cid, pid, name, onDeleted }) => {
  const [open, setOpen] = useState(false);
  const del = async () => {
    try {
      const { data } = await api.delete(`/customers/${cid}/anlop/ports/${pid}`);
      toast.success(`Havnen «${name}» er slettet`);
      setOpen(false);
      onDeleted(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (<>
    <Button variant="outline" size="sm" className="text-red-700 hover:bg-red-50" onClick={() => setOpen(true)} data-testid="anlop-delete-port"><Trash2 className="w-4 h-4 mr-1.5" />Slett havn</Button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-testid="anlop-delete-port-dialog">
        <DialogHeader>
          <DialogTitle>Slette «{name}»?</DialogTitle>
          <DialogDescription>Anløpslisten og teaseren for denne havnen slutter å vise innhold på nettsiden. Super Admin kan gjenopprette havnen senere.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="anlop-delete-port-cancel">Avbryt</Button>
          <Button className="bg-red-600 hover:bg-red-700" onClick={del} data-testid="anlop-delete-port-confirm">Slett havn</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>);
};

export const AnlopPanel = ({ cid, setLocal, settings, onSettings }) => {
  const [ports, setPorts] = useState(null);
  const [pid, setPid] = useState("main");
  const load = useCallback(() => api.get(`/customers/${cid}/anlop/ports`).then((r) => setPorts(r.data)).catch((e) => toast.error(errMsg(e))), [cid]);
  useEffect(() => { load(); }, [load]);
  if (!ports) return <Loading />;
  const multi = ports.ports.length > 1 || ports.can_add;
  const cur = ports.ports.find((p) => p.id === pid) || ports.ports[0];
  const onSaved = (s) => {
    if (cur.id === "main") setLocal("anlop", s);
    if (s.port_name && s.port_name !== cur.name) setPorts((p) => ({ ...p, ports: p.ports.map((x) => (x.id === cur.id ? { ...x, name: s.port_name } : x)) }));
  };
  return (
    <div className="space-y-8">
      <PortTabs cid={cid} ports={ports} pid={cur.id} setPid={setPid} onPorts={setPorts} />
      {settings && ports.ports.length > 1 && <RegionCard cid={cid} ports={ports} onPorts={setPorts} />}
      <PortEditor key={cur.id} cid={cid} port={cur} multi={multi} onSaved={onSaved} settings={settings} onSettings={onSettings}
        onDeleted={(d) => { setPorts(d); setPid("main"); }} />
    </div>
  );
};

const SourceSection = ({ cid, pid, s, status, setAll }) => {
  const fromFile = status?.source_type === "file" && !s.source_url;
  const loaded = status?.rows > 0 && (!!s.source_url || fromFile);
  const src = fromFile ? `Hentet fra filen «${status.file_name || "opplastet fil"}»` : "Hentet fra Google";
  const [open, setOpen] = useState(!loaded);
  return (
    <section data-testid="anlop-source-section">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="w-full text-left flex items-start justify-between gap-3 group" data-testid="anlop-source-toggle">
        <H n={1} title="Datakilde" hint={loaded && !open ? `${src} – ${status.rows} anløp. Trykk for å endre kilden.` : "Google Sheets eller Google Dokument som er delt med «Alle med lenken»."} />
        <ChevronDown className={`w-5 h-5 mt-1.5 text-slate-500 shrink-0 transition-transform group-hover:text-slate-900 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <SourceCard cid={cid} pid={pid} settings={s} status={status} onChange={setAll} />}
    </section>
  );
};

const PortEditor = ({ cid, port, multi, onSaved, onDeleted, settings, onSettings }) => {
  const [docs, setDocs] = useState(false);
  const pid = port.id;
  const { data, saving, rev, update, setAll } = useAnlop(cid, pid, onSaved);
  if (!data) return <Loading />;
  const s = data.settings;
  const code = (attr) => anlopCode(port.key, attr);
  if (!settings) {
    return (
      <div className="space-y-12" data-testid="anlop-panel">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900" data-testid="anlop-heading">{multi ? `${s.port_name} – ${s.title || "Anløpsliste"}` : s.title || "Anløpsliste"}</h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">Listen oppdateres automatisk hvert 5. minutt. Google-lenke, tittel, utseende og embed-koder finner du under Innstillinger.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setDocs(true)} data-testid="anlop-docs-button"><Info className="w-4 h-4 mr-1.5" />Slik bruker du det</Button>
        </div>
        {!(data.status?.rows > 0) && (
          <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="anlop-empty-banner">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Anløpslisten er tom fordi det ikke er lagt inn noen Google-lenke ennå. <button type="button" onClick={onSettings} className="underline font-medium" data-testid="anlop-empty-settings-link">Gå til Innstillinger</button> og lim inn lenken under «Datakilde», så dukker anløpene opp her.</span>
          </div>
        )}
        <section><H title="Forhåndsvisning" hint="Slik ser widgetene ut på nettsiden." />
          <AnlopPreview cid={port.key} rev={rev} />
        </section>
        <AnlopDocs open={docs} onOpenChange={setDocs} />
      </div>
    );
  }
  return (
    <div className="space-y-12" data-testid="anlop-settings-panel">
      <div className="flex flex-wrap items-center justify-end gap-3">
          <span className="text-xs text-slate-500 inline-flex items-center gap-1.5" data-testid="anlop-save-status">
            {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Lagrer…</> : "Alt er lagret"}
          </span>
          {pid !== "main" && <DeletePort cid={cid} pid={pid} name={s.port_name} onDeleted={onDeleted} />}
      </div>
      <SourceSection cid={cid} pid={pid} s={s} status={data.status} setAll={setAll} />
      <section><H n={2} title="Tittel og tekst" hint="Tittelen brukes på fanen her og øverst i anløpslisten." />
        <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-5 space-y-5">
          {multi && (
            <label className="space-y-1.5 block sm:max-w-sm">
              <span className="text-sm font-medium text-slate-700">Navn på havnen</span>
              <Input value={s.port_name} maxLength={60} onChange={(e) => update({ port_name: e.target.value })} data-testid="anlop-port-name-input" />
              <span className="text-xs text-slate-500">Brukes på fanen over og i kalendernavnet «Anløpsliste {s.port_name}».</span>
            </label>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="space-y-1.5 block">
              <span className="text-sm font-medium text-slate-700">Tittel</span>
              <Input value={s.title} maxLength={40} onChange={(e) => update({ title: e.target.value })} data-testid="anlop-title-input" placeholder="F.eks. Anløpsoversikt, Ankomster, Cruise & anløp" />
            </label>
            <label className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-4 py-3 self-end">
              <span className="text-sm text-slate-700">Vis tittelen øverst i anløpslisten</span>
              <Switch checked={s.show_title} onCheckedChange={(v) => update({ show_title: v }, 0)} data-testid="anlop-show-title-switch" />
            </label>
          </div>
          <label className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-4 py-3">
            <span className="text-sm text-slate-700">Vis knappen «Abonnér til din kalender» nederst i listen<span className="block text-xs text-slate-500">Besøkende skanner en QR-kode og får anløpene inn i mobilkalenderen som «Anløpsliste» etterfulgt av havnens navn.</span></span>
            <Switch checked={!!s.ics_enabled} onCheckedChange={(v) => update({ ics_enabled: v }, 0)} data-testid="anlop-ics-switch" />
          </label>
          <label className="space-y-1.5 block">
            <span className="text-sm font-medium text-slate-700">Ingress over listen (valgfritt)</span>
            <textarea value={s.intro} maxLength={600} rows={2} onChange={(e) => update({ intro: e.target.value })} data-testid="anlop-intro-input"
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-400"
              placeholder="Her finner du oversikt over alle innmeldte anløp i år og kommende år." />
          </label>
          <div className="space-y-1.5">
            <span className="text-sm font-medium text-slate-700">Tekst under anløpslisten</span>
            <NoteEditor html={s.note_html} hideToggle onHtml={(h) => update({ note_html: h }, 1000)} testId="anlop-note-editor" />
          </div>
        </div>
      </section>
      <section><H n={3} title="Innstillinger for «Neste anløp»" hint="Farger, fonter og størrelser for den kompakte widgeten til forsiden." />
        <TeaserSettings t={s.teaser} onChange={(t, d) => update({ teaser: { ...s.teaser, ...t } }, d)} />
      </section>
      <section><H n={4} title={multi ? `Embed-koder for ${s.port_name}` : "Embed-koder"} hint="Lim inn koden i en HTML-widget (WordPress/Elementor). Begge henter data fra samme dokument." />
        <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-5 space-y-5">
          <CopyBox label="Full anløpsliste" testId="anlop-embed-list" text={code("anlop")} />
          <CopyBox label="Neste anløp (teaser til forsiden)" testId="anlop-embed-teaser" text={code("anlop-teaser")} />
          <p className="text-xs text-slate-500">Bruker du både kalkulatoren og anløpslisten{multi && ", eller lister for flere havner,"} på samme side, holder det med én &lt;script&gt;-linje.</p>
        </div>
      </section>
    </div>
  );
};
